import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Avatar,
  Card,
  CardContent,
  CardActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  GridView as GridViewIcon,
  TableChart as TableChartIcon,
  ContactMail as ContactMailIcon,
  Clear as ClearIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import CustomerPrimaryAddressBookDetailsModal from '../../AddOrUpdate/components/CustomerPrimaryAddressBookDetailsModal';

const CustomerAddressBookDetailsView = ({ reactTableData, isNoRecordFound }) => {
  const rows = useMemo(() => {
    return reactTableData && reactTableData.tableRowsData ? reactTableData.tableRowsData : [];
  }, [reactTableData]);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal State
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isTitle, setTitle] = useState(null);

  const handleAdd = () => {
    setSelectedAddress(null);
    setTitle("Add - Customer Address Book Details");
    setViewDetailPopup(true);
  };

  const handleEdit = (addressItem) => {
    setSelectedAddress(addressItem);
    setTitle("Edit - Customer Address Book Details");
    setViewDetailPopup(true);
  };

  // Filter rows based on search term
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((item) => {
      const name = item?.customerName ? item.customerName.toLowerCase() : '';
      const code = item?.customerCode ? item.customerCode.toLowerCase() : '';
      const mobile = item?.mobileNo ? String(item.mobileNo).toLowerCase() : '';
      const title = item?.addressBookName ? item.addressBookName.toLowerCase() : '';
      const address = item?.address ? item.address.toLowerCase() : '';
      return (
        name.includes(term) ||
        code.includes(term) ||
        mobile.includes(term) ||
        title.includes(term) ||
        address.includes(term)
      );
    });
  }, [rows, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const getLabelIcon = (labelName) => {
    if (!labelName) return <PlaceIcon sx={{ fontSize: 14 }} />;
    const name = String(labelName).toLowerCase();
    if (name.includes('home')) return <HomeIcon sx={{ fontSize: 14 }} />;
    if (name.includes('work') || name.includes('office')) return <WorkIcon sx={{ fontSize: 14 }} />;
    return <PlaceIcon sx={{ fontSize: 14 }} />;
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Page Header & Controls Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          {/* Title & Count */}
          <Grid item xs={12} md={5} display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                bgcolor: '#667eea',
                width: 46,
                height: 46,
                boxShadow: '0 4px 10px rgba(102, 126, 234, 0.35)',
              }}
            >
              <ContactMailIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b', fontSize: '1.2rem', lineHeight: 1.2 }}>
                Active Customer Address Book Details
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                Manage customer primary and secondary address records
              </Typography>
            </Box>
            <Chip
              label={`${filteredRows.length} Total`}
              size="small"
              sx={{
                bgcolor: '#e0e7ff',
                color: '#4338ca',
                fontWeight: 700,
                fontSize: '0.75rem',
                ml: 1,
              }}
            />
          </Grid>

          {/* Search, View Toggle, Add Action */}
          <Grid item xs={12} md={7} display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1.5} flexWrap="wrap">
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search Customer, Code, Address..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  fontSize: '0.875rem',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Table / Grid Toggle Buttons */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) setViewMode(newMode);
              }}
              size="small"
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                border: '1px solid #e2e8f0',
                p: '2px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: '#64748b',
                  '&.Mui-selected': {
                    bgcolor: '#667eea',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#5a67d8',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <TableChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Table View
              </ToggleButton>
              <ToggleButton value="grid">
                <GridViewIcon sx={{ fontSize: 18, mr: 0.5 }} /> Grid View
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Add Address Book Button */}
            <Button
              variant="contained"
              onClick={handleAdd}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2.5,
                px: 2.5,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b4693 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.45)',
                },
              }}
            >
              Add Address
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content: Table View vs Grid View */}
      {isNoRecordFound || filteredRows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#fff',
          }}
        >
          <PersonIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography variant="h6" color="textSecondary" fontWeight={600}>
            No Address Records Found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Try adjusting your search criteria or click "Add Address" to create a new entry.
          </Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        /* TABLE VIEW */
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Customer Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Customer Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Mobile No.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Title / Label</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Address</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => {
                  const serialNo = page * rowsPerPage + index + 1;
                  const isPrimary = row.addressType === 1 || row.addressType === '1';
                  return (
                    <TableRow
                      key={row.hubCustomerAddBookId || row.id || index}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{serialNo}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: isPrimary ? '#dcfce7' : '#fef3c7',
                              color: isPrimary ? '#15803d' : '#b45309',
                              fontSize: '0.875rem',
                              fontWeight: 700,
                            }}
                          >
                            {row.customerName ? row.customerName.charAt(0).toUpperCase() : 'A'}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {row.customerName || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.customerCode || 'N/A'}
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#334155',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} color="#475569">
                          <PhoneIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                          <Typography variant="body2">{row.mobileNo || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.addressBookName ? (
                          <Chip
                            icon={getLabelIcon(row.addressBookName)}
                            label={row.addressBookName}
                            size="small"
                            sx={{
                              bgcolor: '#f0f9ff',
                              color: '#0369a1',
                              fontWeight: 600,
                              fontSize: '0.725rem',
                              borderRadius: 1.5,
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isPrimary ? 'Primary' : 'Secondary'}
                          size="small"
                          sx={{
                            bgcolor: isPrimary ? '#dcfce7' : '#fef3c7',
                            color: isPrimary ? '#15803d' : '#b45309',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Tooltip title={row.address || ''} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#475569',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.address || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Address Details">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            sx={{
                              color: '#667eea',
                              bgcolor: '#e0e7ff',
                              '&:hover': { bgcolor: '#c7d2fe' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </Paper>
      ) : (
        /* GRID VIEW (CARD VIEW) */
        <Box>
          <Grid container spacing={2.5}>
            {paginatedRows.map((row, index) => {
              const isPrimary = row.addressType === 1 || row.addressType === '1';
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.hubCustomerAddBookId || row.id || index}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.25s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.1)',
                        borderColor: '#cbd5e1',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      {/* Top Header: Initial Avatar + Address Type Badge */}
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: isPrimary ? '#dcfce7' : '#fef3c7',
                            color: isPrimary ? '#15803d' : '#b45309',
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                          }}
                        >
                          {row.customerName ? row.customerName.charAt(0).toUpperCase() : 'A'}
                        </Avatar>
                        <Chip
                          label={isPrimary ? 'Primary' : 'Secondary'}
                          size="small"
                          sx={{
                            bgcolor: isPrimary ? '#dcfce7' : '#fef3c7',
                            color: isPrimary ? '#15803d' : '#b45309',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            borderRadius: 1.5,
                          }}
                        />
                      </Box>

                      {/* Customer Name */}
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#0f172a', fontSize: '1rem', mb: 0.5, lineHeight: 1.3 }}>
                        {row.customerName || 'Unnamed Customer'}
                      </Typography>

                      {/* Code Badge & Label */}
                      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                        <Chip
                          icon={<BadgeIcon sx={{ fontSize: '14px !important', color: '#0284c7 !important' }} />}
                          label={row.customerCode || 'No Code'}
                          size="small"
                          sx={{
                            bgcolor: '#f0f9ff',
                            color: '#0369a1',
                            fontWeight: 600,
                            fontSize: '0.725rem',
                            borderRadius: 1.5,
                          }}
                        />
                        {row.addressBookName && (
                          <Chip
                            icon={getLabelIcon(row.addressBookName)}
                            label={row.addressBookName}
                            size="small"
                            sx={{
                              bgcolor: '#fdf4ff',
                              color: '#c026d3',
                              fontWeight: 600,
                              fontSize: '0.725rem',
                              borderRadius: 1.5,
                            }}
                          />
                        )}
                      </Box>

                      {/* Info Rows */}
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon sx={{ fontSize: 16, color: '#64748b' }} />
                          <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                            {row.mobileNo || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <LocationOnIcon sx={{ fontSize: 16, color: '#64748b', mt: 0.2 }} />
                          <Tooltip title={row.address || ''} placement="top">
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#475569',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontSize: '0.8rem',
                                lineHeight: 1.4,
                              }}
                            >
                              {row.address || 'No Address Provided'}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>

                    {/* Card Actions Footer */}
                    <CardActions sx={{ px: 2.5, pb: 2, pt: 0, justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9' }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleEdit(row)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: '#667eea',
                          borderRadius: 2,
                          px: 1.5,
                          '&:hover': { bgcolor: '#f0f3ff' },
                        }}
                      >
                        Edit Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination for Grid View */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <TablePagination
                rowsPerPageOptions={[8, 16, 32, 64]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </Box>
      )}

      {/* Add or Update Customer Address Book Details Modal */}
      {viewDetailPopup && (
        <CustomerPrimaryAddressBookDetailsModal
          modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={selectedAddress}
          isTitle={isTitle}
        />
      )}
    </Box>
  );
};

CustomerAddressBookDetailsView.propTypes = {
  reactTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
};

export default CustomerAddressBookDetailsView;
